import {
    VERIFY_SERIAL,
    VERIFY_SERIAL_SUCCESS,
    VERIFY_SERIAL_FAILED,
    REGISTER_VEHICLE,
    REGISTER_VEHICLE_SUCCESS,
    REGISTER_VEHICLE_FAILED,
    VERIFY_QR,
    VERIFY_QR_SUCCESS,
    VERIFY_QR_FAILED,
    CLEAR_SERIAL_ERROR,
    ACTIVATE_REGISTRATION,
    ACTIVATE_REGISTRATION_SUCCESS,
    ACTIVATE_REGISTRATION_FAILED
} from '../store/types';

import store from '../store/store';
import { firebase } from '../config/configureFirebase';

// Fallback defaults - used only until getSerialSeries() below has loaded the real,
// admin-configurable list (which always includes these three plus any custom series added
// via the admin panel). The server (register_vehicle, generate_serial_numbers) is the
// authoritative source for both the valid-prefix list and the pre-paid flag; these consts
// just avoid a blank dropdown / a wrong routing guess before that fetch resolves.
export const SERIAL_PREFIXES = ['SNO', 'AMZ', 'RHL'];
export const SERIAL_DIGITS = 7;

// SNO / AMZ serials skip the payment gateway (pre-paid batches); RHL still requires payment.
export const PAYMENT_EXEMPT_PREFIXES = ['SNO', 'AMZ'];
export const isPaymentExemptSerial = (serialNumber) => {
    if (!serialNumber) return false;
    const prefix = String(serialNumber).split('-')[0].toUpperCase();
    return PAYMENT_EXEMPT_PREFIXES.includes(prefix);
};

// Exported (unlike the other module-private helpers here) so callers that need to build a
// Cloud Function URL themselves - e.g. Renew.js's SkipCash redirect, which can't go through
// fetch() since it's a top-level browser navigation - resolve the same host every other
// action here already does, instead of naively using window.location.origin (wrong on
// localhost during development, where there's no Firebase Hosting rewrite to proxy through).
export const getHost = () => {
    const { config } = firebase;
    const settings = store.getState().settingsdata.settings;
    const companyWebsite = settings && settings.CompanyWebsite ? settings.CompanyWebsite.replace(/\/$/, '') : null;
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : null;
    if (companyWebsite && origin && companyWebsite === origin) {
        return origin;
    }
    return `https://${config.projectId}.web.app`;
};

export const verifySerial = (serialNumber) => async (dispatch) => {
    dispatch({ type: VERIFY_SERIAL, payload: null });
    try {
        const host = getHost();
        const response = await fetch(`${host}/verify_serial`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ serialNumber })
        });
        const result = await response.json();
        if (result.valid) {
            dispatch({ type: VERIFY_SERIAL_SUCCESS, payload: result });
        } else {
            dispatch({ type: VERIFY_SERIAL_FAILED, payload: result.error || 'Invalid serial number' });
        }
        return result;
    } catch (error) {
        dispatch({ type: VERIFY_SERIAL_FAILED, payload: error.message });
        return { valid: false, error: error.message };
    }
};

export const registerVehicle = (data) => async (dispatch) => {
    dispatch({ type: REGISTER_VEHICLE, payload: null });
    try {
        const host = getHost();
        const response = await fetch(`${host}/register_vehicle`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        const result = await response.json();
        if (result.success) {
            dispatch({ type: REGISTER_VEHICLE_SUCCESS, payload: result });
        } else {
            dispatch({ type: REGISTER_VEHICLE_FAILED, payload: result.error || 'Registration failed' });
        }
        return result;
    } catch (error) {
        dispatch({ type: REGISTER_VEHICLE_FAILED, payload: error.message });
        return { success: false, error: error.message };
    }
};

export const verifyQRCode = (qrCode) => async (dispatch) => {
    dispatch({ type: VERIFY_QR, payload: null });
    try {
        const host = getHost();
        const response = await fetch(`${host}/verify_qr`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ qrCode })
        });
        const result = await response.json();
        if (result.valid) {
            dispatch({ type: VERIFY_QR_SUCCESS, payload: result });
        } else {
            dispatch({ type: VERIFY_QR_FAILED, payload: result.error || 'Invalid QR code' });
        }
        return result;
    } catch (error) {
        dispatch({ type: VERIFY_QR_FAILED, payload: error.message });
        return { valid: false, error: error.message };
    }
};

export const initiateMaskedCall = (callerPhone, serial) => async (dispatch) => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/initiate_masked_call`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ caller_phone: callerPhone, serial })
        });
        const result = await response.json();
        return result;
    } catch (error) {
        return { valid: false, error: error.message };
    }
};

export const clearSerialError = () => (dispatch) => {
    dispatch({ type: CLEAR_SERIAL_ERROR, payload: null });
};

// Generates `count` random alpha-numeric serial numbers of the form
// PREFIX-<7 random digits> (e.g. SNO-1746262), guaranteed unique against every
// serial number already stored in the database. Routed through the
// `generate_serial_numbers` Cloud Function (Admin SDK) rather than writing to
// Firestore directly from the client - firestore.rules denies direct client
// writes to `serialNumbers` (see the "All writes ... go exclusively through
// Cloud Functions" comment there), so a direct write here would always fail
// with "Missing or insufficient permissions", admin session or not.
// `startSequence` is where this run's per-unit "<batchNumber>-<seq>" numbering starts
// (GenerateQr.js's "Starting Batch Sequence #" field) - passed through so the server can
// store each serial's actual batchSequence offset by it, not always starting at 1.
export const generateUniqueSerialNumbers = (prefix, count, batchNumber, startSequence) => async () => {
    try {
        // Prefix validity is decided server-side against the serialSeries collection (see
        // generate_serial_numbers) - not checked against the old hardcoded SERIAL_PREFIXES
        // here, since that would reject every admin-added custom series before the request
        // even goes out.
        const host = getHost();
        const response = await fetch(`${host}/generate_serial_numbers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prefix, count, batchNumber, startSequence })
        });
        const result = await response.json();
        if (!result || !result.success) {
            return { success: false, error: (result && result.error) || 'Failed to generate serial numbers' };
        }
        return result;
    } catch (error) {
        console.error("Error generating serial numbers: ", error);
        return { success: false, error: error.message };
    }
};

// Lists every serial series/prefix (the 3 built-in defaults plus any admin-added custom
// ones), each with its prePaid flag - drives GenerateQr.js's series dropdown and the
// "add custom series" admin form. The server auto-seeds SNO/AMZ/RHL into serialSeries the
// first time this is called if that collection is still empty.
export const getSerialSeries = () => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/get_serial_series`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        return await response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// Admin action: registers a new custom series/prefix (e.g. a new pre-paid partner batch)
// so it becomes selectable in GenerateQr.js and valid for register_vehicle going forward.
export const addSerialSeries = (prefix, prePaid) => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/add_serial_series`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prefix, prePaid })
        });
        return await response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// Reverse of verifySerial: given a batch number, returns every serial number generated into that
// batch's PDF (in print order), for auditing a whole run rather than backtracking one sticker.
export const getSerialsByBatch = (batchNumber) => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/get_serials_by_batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ batchNumber })
        });
        return await response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// Lists every batch ever generated (one entry per batchNumber, with its serial count and earliest
// createdAt) for the Batch Details admin page's landing list.
export const getAllBatches = () => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/get_all_batches`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        return await response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// Email verification (registration, account deletion, new-device login). No Redux state of its
// own — callers just await the { success | error } result directly, same as initiateMaskedCall.
export const requestEmailOtp = (email) => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/request_email_otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        return await response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
};

export const verifyEmailOtp = (email, otp) => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/verify_email_otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, otp })
        });
        return await response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// The five actions below replace what used to be direct RTDB access from the mobile app's
// registration screens - they call the matching Cloud Function endpoints instead (see
// functions/index.js: get_registration_by_id, get_registration_by_serial,
// get_registrations_by_phone, update_registration, delete_registrations).

export const getRegistrationById = (registrationId) => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/get_registration_by_id`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ registrationId })
        });
        return await response.json();
    } catch (error) {
        return { exists: false, error: error.message };
    }
};

export const getRegistrationBySerial = (serialNumber) => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/get_registration_by_serial`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ serialNumber })
        });
        return await response.json();
    } catch (error) {
        return { exists: false, error: error.message };
    }
};

export const getRegistrationsByPhone = (phone) => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/get_registrations_by_phone`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phone })
        });
        return await response.json();
    } catch (error) {
        return { registrations: [], error: error.message };
    }
};

// Mirrors getRegistrationsByPhone above, but by email - used by the rahl.me/renew page,
// which only ever asks for an email (customers see the QR, not the plain serial number).
export const getRegistrationsByEmail = (email) => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/get_registrations_by_email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        return await response.json();
    } catch (error) {
        return { registrations: [], error: error.message };
    }
};

export const updateRegistration = (registrationId, updates) => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/update_registration`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ registrationId, updates })
        });
        return await response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// Deletes exactly ONE registration by registrationId (or serialNumber as a fallback
// identifier for that same record) - never pass phone here, a phone number can own
// multiple registered vehicles and the backend intentionally does not match by it.
export const deleteRegistrations = ({ serialNumber, registrationId } = {}) => async () => {
    try {
        const host = getHost();
        const response = await fetch(`${host}/delete_registrations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ serialNumber, registrationId })
        });
        return await response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// Shared by VehicleSelectScreen and ServiceListScreen (previously ~60 lines of near-identical
// direct-RTDB lookup logic duplicated in each screen). Resolves the current registration by ID
// or serial, then finds every other registration under the same phone number (the server already
// handles the +91/+974 local-number variant matching), and ensures the current registration is
// included even if the phone lookup didn't happen to return it.
export const findMyVehicles = (currentRegId, currentSn, fallbackPhone) => async (dispatch) => {
    try {
        let primaryRecord = null;

        if (currentRegId) {
            const byId = await dispatch(getRegistrationById(currentRegId));
            if (byId && byId.exists) {
                primaryRecord = { registrationId: byId.registrationId, ...byId.data };
            }
        }

        if (!primaryRecord && currentSn) {
            const bySerial = await dispatch(getRegistrationBySerial(currentSn));
            if (bySerial && bySerial.exists) {
                primaryRecord = { registrationId: bySerial.registrationId, ...bySerial.data };
            }
        }

        const phone = (primaryRecord && primaryRecord.phone) || fallbackPhone;

        let vehicles = [];
        if (phone) {
            const byPhone = await dispatch(getRegistrationsByPhone(phone));
            vehicles = (byPhone && byPhone.registrations) || [];
        }

        if (primaryRecord && !vehicles.some(v => v.registrationId === primaryRecord.registrationId)) {
            vehicles = [primaryRecord, ...vehicles];
        }

        return { vehicles, primaryRecord };
    } catch (error) {
        return { vehicles: [], primaryRecord: null, error: error.message };
    }
};

export const activateRegistration = (registrationId) => async (dispatch) => {
    dispatch({ type: ACTIVATE_REGISTRATION, payload: null });
    try {
        const host = getHost();
        const response = await fetch(`${host}/activate_registration`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ registrationId })
        });
        const result = await response.json();
        if (result.success) {
            dispatch({ type: ACTIVATE_REGISTRATION_SUCCESS, payload: result });
        } else {
            dispatch({ type: ACTIVATE_REGISTRATION_FAILED, payload: result.error || 'Activation failed' });
        }
        return result;
    } catch (error) {
        dispatch({ type: ACTIVATE_REGISTRATION_FAILED, payload: error.message });
        return { success: false, error: error.message };
    }
};
